import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import { getModuleBySlug, getModulesByGroup } from '../../data/modulesData';

const ModulePage = () => {
  const { slug } = useParams();
  const module = getModuleBySlug(slug);

  if (!module) {
    return <Navigate to="/modulos" replace />;
  }

  const IconComponent = LucideIcons[module.icon] || LucideIcons.Box;
  const relatedModules = getModulesByGroup(module.group).filter(
    (m) => m.slug !== module.slug
  );

  // Build SEO metadata
  const metaDescription = `${module.tagline}. ${module.description}`.slice(0, 158);
  const featureKeywords = module.features.map((f) => f.title).join(', ');
  const metaKeywords = `${module.name}, ${module.group}, ${featureKeywords}, asisant`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${module.name} – asisant`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: module.description,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      price: '0',
    },
    featureList: module.features.map((f) => f.title).join('; '),
    provider: {
      '@type': 'Organization',
      name: 'asisant',
      url: 'https://asisant.com',
    },
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`module-page-${module.slug}`}>
      <SEO
        title={`${module.name} – ${module.tagline}`}
        description={metaDescription}
        keywords={metaKeywords}
        canonical={`/modulos/${module.slug}`}
        type="product"
        structuredData={structuredData}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden bg-dark-ellipse">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <Link
              to="/modulos"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors"
              data-testid="module-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Todos los módulos
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
              >
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <span className="inline-block px-4 py-2 bg-white/10 text-white/90 rounded-full text-sm font-medium backdrop-blur-sm border border-white/20">
                {module.group}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {module.name}
            </h1>
            <p className="text-xl text-white/80 mb-4 max-w-3xl leading-relaxed">
              {module.tagline}
            </p>
            <p className="text-base text-[#4E95D9] font-medium mb-10">
              {module.heroHighlight}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`/contacto?source=${module.slug}`}>
                <Button
                  size="lg"
                  className="text-white font-semibold px-8 py-6 rounded-full transition-all hover:scale-105 hover:shadow-2xl"
                  style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                  data-testid="module-cta-demo"
                >
                  Solicitar demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contacto?source=experto">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-6 rounded-full backdrop-blur-sm"
                  data-testid="module-cta-expert"
                >
                  Hablar con un experto
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPCIÓN */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            ¿Qué es {module.name}?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {module.description}
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#4E95D9]/10 text-[#2B6FA8] rounded-full text-sm font-medium mb-4">
              Capacidades
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Todo lo que necesitas, <span className="text-[#4E95D9]">en un solo módulo</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {module.features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#4E95D9]/40 hover:shadow-xl hover:shadow-[#4E95D9]/10 transition-all duration-300 hover:-translate-y-1"
                data-testid={`module-feature-${idx}`}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                >
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[#4E95D9]/10 text-[#2B6FA8] rounded-full text-sm font-medium mb-4">
                Beneficios
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                ¿Por qué elegir este módulo?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Diseñado para {module.targetUsers.toLowerCase()}
              </p>

              <ul className="space-y-4">
                {module.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3" data-testid={`module-benefit-${idx}`}>
                    <div
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                      style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                    >
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700 leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div
                className="rounded-3xl p-8 shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
              >
                <IconComponent className="w-16 h-16 text-white/90 mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">{module.name}</h3>
                <p className="text-white/90 leading-relaxed mb-6">{module.tagline}</p>
                <div className="pt-6 border-t border-white/20">
                  <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Para</p>
                  <p className="text-white font-medium">{module.targetUsers}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED MODULES */}
      {relatedModules.length > 0 && (
        <section className="py-24 bg-[#f7f9fc]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="inline-block px-4 py-2 bg-[#4E95D9]/10 text-[#2B6FA8] rounded-full text-sm font-medium mb-4">
                  Categoría {module.group}
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  Módulos relacionados
                </h2>
              </div>
              <Link
                to="/modulos"
                className="inline-flex items-center gap-2 text-[#2B6FA8] hover:text-[#4E95D9] font-medium"
              >
                Ver todos los módulos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedModules.map((m) => {
                const RelIcon = LucideIcons[m.icon] || LucideIcons.Box;
                return (
                  <Link
                    key={m.slug}
                    to={`/modulos/${m.slug}`}
                    className="group bg-white rounded-2xl p-6 border border-[#e3e4e6] hover:border-[#4E95D9] transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    data-testid={`related-module-${m.slug}`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                      style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                    >
                      <RelIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#2B6FA8] transition-colors">
                      {m.name}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.tagline}</p>
                    <div className="mt-4 inline-flex items-center text-[#2B6FA8] text-sm font-medium">
                      Saber más
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-24 bg-dark-ellipse relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            ¿Listo para activar {module.name}?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Agenda una demo personalizada y descubre cómo este módulo se integra con el resto de tu operación.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/contacto?source=${module.slug}`}>
              <Button
                size="lg"
                className="bg-white hover:bg-white/90 text-[#2B6FA8] font-semibold px-8 py-6 rounded-full transition-all hover:scale-105"
                data-testid="module-final-cta"
              >
                Solicitar demo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ModulePage;
